<template>
  <div id="base-editor">
    <LoadingSpinner
      v-if="domainStore.loadingDomain || domainStore.loadingOverrides"
      class="mt-8 p-4"
    />
    <div v-else class="mt-4">
      <article id="editor-section">
        <form @submit.prevent="handleSubmit" v-if="domainStore.domain">
          <TextEditor
            :value="editVal"
            :showMenu="true"
            role="document"
            :height="overrideType === 'agent_about_me' ? '500px' : '300px'"
            placeholder="Create a suggestion!"
            :id="`${overrideType}${overrideSubkey ? '-' + overrideSubkey : ''}-editor`"
            ref="editor"
            @update:value="(newv) => handleUpdate(newv)"
            class="editor"
          />
          <PersonalInfoDisclaimer />
          <button
            type="submit"
            class="py-2 my-2 font-bold rounded-md transition ease-in-out"
            :class="
              isCompliant
                ? ' bg-[#FEE685] text-gray-900 hover:bg-[#FFD560]'
                : 'cursor-not-allowed bg-gray-300 text-gray-800 hover:bg-gray-300 hover:text-black'
            "
            :disabled="!isCompliant"
          >
            Submit Suggestion
          </button>
          <button
            v-if="editVal"
            type="reset"
            class="py-2 m-2 font-bold rounded-md text-white bg-gray-500 hover:bg-gray-800 transition ease-in-out"
            @click="clear()"
          >
            Start From Scratch
          </button>
          <div
            v-if="compliance.midCheck.value == true"
            class="flex flex-row w-full p-4 bg-white gap-4 items-center"
          >
            <LoadingSpinner />
            <p>Checking your suggestion for compliance...</p>
          </div>
          <div
            v-if="posting"
            class="flex flex-row w-full p-4 bg-white gap-4 items-center"
          >
            <LoadingSpinner />
            <p>Submitting your Suggestion...</p>
          </div>
          <div
            v-if="invalidTerms.length > 0 || generalViolations.length > 0"
            class="border-2 border-red-300 bg-red-200 p-2 rounded-xl my-1"
          >
            <div v-if="invalidTerms.length > 0">
              <p class="mb-0">
                {{ error }} Please remove or reword the following:
              </p>
              <ul
                v-for="(term, index) in invalidTerms"
                :key="`${term.replaceAll(' ', '_')}_${index}`"
                class="list-inside list-disc"
              >
                <li>{{ term }}</li>
              </ul>
            </div>
            <div v-if="generalViolations.length > 0">
              <p class="mb-0">{{ error }} Please address the following:</p>
              <ul
                v-for="(msg, index) in generalViolations"
                :key="`${msg.replaceAll(' ', '_')}_${index}`"
                class="list-inside list-disc"
              >
                <li>{{ msg }}</li>
              </ul>
            </div>
          </div>
          <div v-else-if="error" class="">
            {{ error }}
          </div>
          <div
            v-if="spellingStatus === 'failed'"
            class="border-2 border-orange-300 bg-orange-200 p-2 my-1 rounded-xl"
          >
            <p class="mb-0">
              There may be misspelled words, please check before saving.
            </p>
          </div>
          <div
            v-if="isCompliant"
            class="border-2 border-green-300 bg-green-200 p-2 my-1 rounded-xl"
          >
            <p class="mb-0">This text is compliant!</p>
          </div>
        </form>
      </article>
      <div class="flex flex-col">
        <SuggestionHistory
          id="pending-suggestion-list"
          label="Pending Suggestions"
          :overrides="domainStore.suggestions ?? []"
        />
        <SuggestionHistory
          id="content-history-list"
          label="Content History"
          :overrides="contentHistory"
          empty-message="There is no content history"
        />
      </div>
    </div>

    <Modal v-show="showConfirm" @close="showConfirm = false">
      <template v-slot:header>
        <h1 class="text-xl font-bold uppercase">Are you sure?</h1>
      </template>
      <template v-slot:body>
        <p>
          This suggestion will be submitted to this domain. The agent will see
          that you,
          <span class="font-mono text-sm text-orange-800">{{
            userStore.user.email
          }}</span
          >, created the suggestion.
        </p>
      </template>
      <template v-slot:footer>
        <div class="flex gap-2 justify-between">
          <button
            @click="showConfirm = false"
            class="bg-gray-100 hover:bg-gray-200 text-gray-900"
          >
            Don't submit
          </button>
          <button
            @click="
              showConfirm = false;
              post();
            "
          >
            Submit suggestion
          </button>
        </div>
      </template>
    </Modal>
  </div>
  <Onboarding page="editor" />
</template>

<script setup>
import TextEditor from "@mirus/tiptap-editor";
import { useDebounceFn } from "@vueuse/core";
import { useCookies } from "@vueuse/integrations/useCookies";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import PersonalInfoDisclaimer from "@/components/editor/PersonalInfoDisclaimer.vue";
import SuggestionHistory from "@/components/editor/SuggestionHistory.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Modal from "@/components/Modal.vue";
import Onboarding from "@/components/Onboarding.vue";
import { useCompliance } from "@/composables/useCompliance";
import useDomainStore from "@/stores/domain";
import useNotificationStore from "@/stores/notifications";
import useUserStore from "@/stores/user";

const route = useRoute();
const domainStore = useDomainStore();
const notificationStore = useNotificationStore();
const userStore = useUserStore();
const compliance = useCompliance();

const posting = ref(false);
const error = ref("");
const isCompliant = ref(false);
const editVal = ref("");
const showConfirm = ref(false);

const props = defineProps({
  overrideKey: {
    type: String,
    required: true,
  },
  overrideType: {
    type: String,
    required: true,
  },
  overrideSubkey: {
    type: String,
    required: false,
  },
});

const invalidTerms = computed(() => {
  const { collapsedViolations = [], sanitations = [] } =
    compliance.parsedResult.value ?? {};
  return [...collapsedViolations, ...sanitations].map(({ value }) => value);
});

const generalViolations = computed(
  () => compliance.parsedResult.value.generalViolations ?? [],
);
const spellingStatus = computed(() => {
  if (compliance.parsedResult.value?.spellingMistakes) {
    return compliance.parsedResult.value.spellingMistakes?.length < 1
      ? "passed"
      : "failed";
  } else {
    return "unchecked";
  }
});

const contentHistory = computed(() => {
  const history = domainStore.overrideHistory ?? [];
  const activeDco = domainStore.activeDco;
  if (history.length) {
    const suggestionIsActive = history.some((s) => s.status === "approved");
    if (!suggestionIsActive && activeDco) {
      history.unshift(activeDco);
    }
  } else if (activeDco) {
    history.push(activeDco);
  }
  return history;
});
async function clear() {
  editVal.value = "";
  compliance.clearCompliance();
  isCompliant.value = false;
}

const handleUpdate = useDebounceFn(async (text) => {
  editVal.value = text;
  checkText(text);
}, 800);

async function checkText(text) {
  isCompliant.value = false;
  try {
    await compliance.checktext(text);
    if (invalidTerms.value.length > 0 || generalViolations.value.length > 0) {
      error.value = "Some of this text is not compliant";
      isCompliant.value = false;
    } else {
      error.value = " ";
      isCompliant.value = true;
    }
  } catch {
    error.value = "There was a problem checking compliance";
    isCompliant.value = false;
  }
}

function handleSubmit() {
  if (userStore.isMirusUser) {
    showConfirm.value = true;
  } else {
    post();
  }
}

async function post() {
  posting.value = true;
  const cookies = useCookies(["csrftoken"]);
  const dcoData = {
    method: "POST",
    headers: {
      "X-CSRFToken": cookies.get("csrftoken"),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      value: editVal.value,
      key: props.overrideKey,
      override_type: props.overrideType,
      status: "pending",
      branch: "master",
      domain: domainStore.domain.name,
      subkey: props.overrideSubkey ?? null,
      requires_review_from: "agent",
    }),
  };
  const response = await fetch(
    `/api/domains/${domainStore.domain.name}/overrides/`,
    dcoData,
  );
  if (!response.ok) {
    posting.value = false;
    notificationStore.addNotification(
      "There was an error submitting your suggestion. Please try again later.",
      "red",
      5000,
    );
    throw new Error("Suggestion failed");
  }
  if (response.ok) {
    notificationStore.addNotification(
      "Your suggestion was submitted for review!",
      "green",
      5000,
    );
    await domainStore.getOverrides(
      domainStore.domain.name,
      props.overrideType,
      props.overrideSubkey ?? null,
    );
    posting.value = false;
    isCompliant.value = false;
    compliance.clearCompliance();
  }
  posting.value = false;
}

onMounted(async () => {
  await domainStore.ensureDomain(route.params.domain);
  await domainStore.getOverrides(
    domainStore.domain.name,
    props.overrideType,
    props.overrideSubkey,
  );
  editVal.value = domainStore.activeDco?.value;
});
</script>

<style>
.editor ul {
  list-style-type: disc;
}
.editor button {
  color: black;
}
</style>
